import { type EditorSDK } from '@wix/editor-platform-sdk-types';
import type { FlowAPI, IHttpClient } from '@wix/yoshi-flow-editor';
import { queryForms } from '@wix/ambassador-forms-v4-form/http';
import { NAMESPACE } from '../constants/namespace';
import { provisionApp } from '@wix/business-manager-api';
import type { ErrorHandlerPublicAPI } from '@wix/fe-essentials-editor/error-handler';
import { type Form } from '@wix/ambassador-forms-v4-form/types';
import { EXPERIMENTS } from '../constants/experiments';
import type { PopulateFormFieldsParams } from './field-population';
import { queryStaffMembers } from '@wix/ambassador-bookings-staff-v1-staff-member/http';
import { queryServices } from '@wix/ambassador-bookings-services-v2-service/http';
import {
  type Service,
  type QueryServicesResponse,
} from '@wix/ambassador-bookings-services-v2-service/types';
import { injectLeadNurturingAppData } from '@wix/ambassador-genie-inject-lead-nurturing-v1-no-entity/http';
import { provisionMeetingsApp } from '@wix/ambassador-meetings-v1-no-entity/http';

export const SCHEDULING_FORM_FIELDS_APP_DEF_ID =
  '3e4e322b-6d9b-4da0-9f53-e22c4a44a49e';
export const BOOKINGS_APP_DEF_ID = '13d21c63-b5ec-5912-8397-c3a5ddb27a97';
export const SERVICES_APP_DEF_ID = 'cc552162-24a4-45e0-9695-230c4931ef40';
export const MEETINGS_APP_DEF_ID = '6646a75c-2027-4f49-976c-58f3d713ed0f';

async function isSchedulingInstalled(sdk: EditorSDK): Promise<boolean> {
  return sdk.document.application.isApplicationInstalled(
    SCHEDULING_FORM_FIELDS_APP_DEF_ID,
    {
      appDefinitionId: SCHEDULING_FORM_FIELDS_APP_DEF_ID,
    },
  );
}

async function isBookingsInstalled(sdk: EditorSDK): Promise<boolean> {
  return sdk.document.application.isApplicationInstalled(BOOKINGS_APP_DEF_ID, {
    appDefinitionId: BOOKINGS_APP_DEF_ID,
  });
}

async function isServicesInstalled(sdk: EditorSDK): Promise<boolean> {
  return sdk.document.application.isApplicationInstalled(SERVICES_APP_DEF_ID, {
    appDefinitionId: SERVICES_APP_DEF_ID,
  });
}

async function isMeetingsInstalled(sdk: EditorSDK): Promise<boolean> {
  return sdk.document.application.isApplicationInstalled(MEETINGS_APP_DEF_ID, {
    appDefinitionId: MEETINGS_APP_DEF_ID,
  });
}

type InstallSchedulingParams = {
  sdk: EditorSDK;
  flowApi: FlowAPI;
};

const injectLNServices = async ({
  httpClient,
  errorHandler,
}: {
  httpClient: IHttpClient;
  errorHandler: ErrorHandlerPublicAPI;
}) => {
  await errorHandler.withErrorHandler(
    () => httpClient.request(injectLeadNurturingAppData({})),
    {
      errorCodesMap: {},
    },
  );
};

async function provisionMeetings({
  httpClient,
  errorHandler,
}: {
  httpClient: IHttpClient;
  errorHandler: ErrorHandlerPublicAPI;
}) {
  await errorHandler
    .withErrorHandler(() => httpClient.request(provisionMeetingsApp({})), {
      errorCodesMap: {},
    })
    .catch((e) => {
      errorHandler.getResolvedError(e);
      throw e;
    });
}

export const installSchedulingApps = async ({
  sdk,
  flowApi,
  hasFormsWithScheduling,
  hasFormsWithServices,
}: {
  sdk: EditorSDK;
  flowApi: FlowAPI;
  hasFormsWithScheduling: boolean;
  hasFormsWithServices: boolean;
}) => {
  const {
    errorHandler: { getResolvedError },
  } = flowApi;
  try {
    const isRemoveBookingsDependencyEnabled = flowApi.experiments.enabled(
      EXPERIMENTS.REMOVE_BOOKINGS_DEPENDENCY,
    );
    const shouldInstallMeetingsForBIF = flowApi.experiments.enabled(
      EXPERIMENTS.BIF_ON_MEETINGS_PHASE_3,
    );
    const shouldSkipBookings = flowApi.experiments.enabled(
      EXPERIMENTS.BIF_INSTALL_ONLY_MEETINGS,
    );
    const promises = [];

    if (hasFormsWithScheduling || hasFormsWithServices) {
      const [
        bookingsInstalled,
        servicesInstalled,
        schedulingInstalled,
        meetingsInstalled,
      ] = await Promise.all([
        isBookingsInstalled(sdk),
        isServicesInstalled(sdk),
        isSchedulingInstalled(sdk),
        isMeetingsInstalled(sdk),
      ]);

      // Install Bookings (unless future FT says to skip)
      if (
        !shouldSkipBookings &&
        (!isRemoveBookingsDependencyEnabled || hasFormsWithScheduling) &&
        !bookingsInstalled
      ) {
        await sdk.application.install('', {
          appDefinitionId: BOOKINGS_APP_DEF_ID,
        });
      }

      // Install Meetings ON TOP OF Bookings when BIF_ON_MEETINGS_PHASE_3 is enabled
      if (
        shouldInstallMeetingsForBIF &&
        hasFormsWithScheduling &&
        !meetingsInstalled
      ) {
        promises.push(
          provisionMeetings({
            httpClient: flowApi.httpClient,
            errorHandler: flowApi.errorHandler,
          }),
        );
      }

      if (hasFormsWithServices && !servicesInstalled) {
        promises.push(
          (async () => {
            await sdk.application.install('', {
              appDefinitionId: SERVICES_APP_DEF_ID,
            });
            await injectLNServices(flowApi);
          })(),
        );
      }

      if (hasFormsWithScheduling && !schedulingInstalled) {
        promises.push(
          sdk.application.install('', {
            appDefinitionId: SCHEDULING_FORM_FIELDS_APP_DEF_ID,
          }),
        );
      }

      await Promise.all(promises);
    }
  } catch (e) {
    const resolvedError = getResolvedError(e);
    console.error(resolvedError);
    return;
  }
};

export async function maybeInstallScheduling(params: InstallSchedulingParams) {
  const {
    flowApi: { httpClient, reportError, errorHandler, experiments },
    sdk,
  } = params;

  try {
    const { hasFormsWithScheduling, hasFormsWithServices } =
      await hasFormsWithSchedulingOrServices(httpClient, errorHandler);

    if (!hasFormsWithScheduling && !hasFormsWithServices) {
      return;
    }

    if (!experiments.enabled(EXPERIMENTS.WIX_SERVICES_INSTALLATION)) {
      if (hasFormsWithScheduling) {
        const schedulingInstalled = await isSchedulingInstalled(sdk);
        if (!schedulingInstalled) {
          provisionApp(SCHEDULING_FORM_FIELDS_APP_DEF_ID);
        }
      }
      return;
    }

    const shouldInstallMeetingsForBIF = experiments.enabled(
      EXPERIMENTS.BIF_ON_MEETINGS_PHASE_3,
    );
    const shouldSkipBookings = experiments.enabled(
      EXPERIMENTS.BIF_INSTALL_ONLY_MEETINGS,
    );
    const isRemoveBookingsDependencyEnabled = experiments.enabled(
      EXPERIMENTS.REMOVE_BOOKINGS_DEPENDENCY,
    );

    const needsBookingsForScheduling =
      hasFormsWithScheduling && !shouldSkipBookings;
    const needsBookingsForServices =
      hasFormsWithServices && !isRemoveBookingsDependencyEnabled;

    if (needsBookingsForScheduling || needsBookingsForServices) {
      const bookingsInstalled = await isBookingsInstalled(sdk);
      if (!bookingsInstalled) {
        await provisionApp(BOOKINGS_APP_DEF_ID);
      }
    }

    if (hasFormsWithScheduling && shouldInstallMeetingsForBIF) {
      const meetingsInstalled = await isMeetingsInstalled(sdk);
      if (!meetingsInstalled) {
        await provisionMeetings({ httpClient, errorHandler });
      }
    }

    if (hasFormsWithScheduling) {
      const schedulingInstalled = await isSchedulingInstalled(sdk);
      if (!schedulingInstalled) {
        provisionApp(SCHEDULING_FORM_FIELDS_APP_DEF_ID);
      }
    }

    if (hasFormsWithServices) {
      const servicesInstalled = await isServicesInstalled(sdk);
      if (!servicesInstalled) {
        await provisionApp(SERVICES_APP_DEF_ID);
      }
    }
  } catch (e: unknown) {
    reportError(e as Error | string);
  }
}

export function getFormSchedulingFields(form: Form) {
  return (
    form.fields?.filter((field) => field.view?.fieldType === 'APPOINTMENT') ??
    []
  );
}

export function getFormServicesDropdownFields(form: Form) {
  return (
    form.fields?.filter(
      (field) => field.view?.fieldType === 'SERVICES_DROPDOWN',
    ) ?? []
  );
}

async function hasFormsWithSchedulingOrServices(
  httpClient: IHttpClient,
  errorHandler: ErrorHandlerPublicAPI,
) {
  try {
    const response = await errorHandler.withErrorHandler(
      () =>
        httpClient?.request(
          queryForms({
            namespace: NAMESPACE,
          }),
        ),
      { errorCodesMap: {} },
    );

    const forms = response.data.forms;

    const result = {
      hasFormsWithScheduling: false,
      hasFormsWithServices: false,
    };

    if (forms) {
      for (const form of forms) {
        if (result.hasFormsWithScheduling && result.hasFormsWithServices) {
          break;
        }
        if (getFormSchedulingFields(form).length > 0) {
          result.hasFormsWithScheduling = true;
        }
        if (getFormServicesDropdownFields(form).length > 0) {
          result.hasFormsWithServices = true;
        }
      }
    }

    return result;
  } catch (e) {
    const resolvedError = errorHandler.getResolvedError(e);
    console.error(resolvedError);
    return { hasFormsWithScheduling: false, hasFormsWithServices: false };
  }
}

export async function getSchedulingData({
  httpClient,
  errorHandler,
}: {
  httpClient: IHttpClient;
  errorHandler: ErrorHandlerPublicAPI;
}) {
  try {
    const queryStaffMembersResponse = await errorHandler.withErrorHandler(
      () => httpClient.request(queryStaffMembers({})),
      {
        errorCodesMap: {},
      },
    );
    const staffMember = queryStaffMembersResponse?.data?.staffMembers?.[0];
    return { staffMember };
  } catch (e) {
    const resolvedError = errorHandler.getResolvedError(e);
    console.error(resolvedError);
    return;
  }
}

export async function populateSchedulingFields(args: PopulateFormFieldsParams) {
  const { form, dataForFieldPopulation } = args;
  const staffMember = dataForFieldPopulation.schedulingData?.staffMember;

  const schedulingFields = getFormSchedulingFields(form);
  schedulingFields.forEach((field) => {
    field.view!.name = 'Consultation';
    field.view!.duration = 30;
    field.view!.staffIds = [staffMember?.resourceId];
    field.view!.format = 'PHONE';
    field.view!.formatDescription = 'Phone call';
    field.view!.formatDescriptionType = 'PHONE';
    field.view!.label = 'Schedule an appointment';
    field.view!.manualApprovalRequired = false;
    field.view!.staffIds = [staffMember?.resourceId];
  });
}

const getServices = async ({
  httpClient,
  errorHandler,
  linguistHeader,
}: {
  httpClient: IHttpClient;
  errorHandler: ErrorHandlerPublicAPI;
  linguistHeader: string;
}): Promise<Service[]> => {
  const queryServicesResponse = (
    await errorHandler.withErrorHandler(
      () =>
        httpClient.request((args) => ({
          ...queryServices({
            query: {
              filter: {
                appId: SERVICES_APP_DEF_ID,
              },
            },
          })(args),
          headers: { 'x-wix-linguist': linguistHeader },
        })),
      { errorCodesMap: {} },
    )
  ).data as QueryServicesResponse;
  return queryServicesResponse.services ?? [];
};

export const getServicesData = async ({
  httpClient,
  errorHandler,
  linguistHeader,
}: {
  httpClient: IHttpClient;
  errorHandler: ErrorHandlerPublicAPI;
  linguistHeader: string;
}) => {
  try {
    let services = await getServices({
      httpClient,
      errorHandler,
      linguistHeader,
    });
    if (!services.length) {
      await injectLNServices({
        httpClient,
        errorHandler,
      });
      services = await getServices({
        httpClient,
        errorHandler,
        linguistHeader,
      });
    }
    return { services };
  } catch (e) {
    const resolvedError = errorHandler.getResolvedError(e);
    console.error(resolvedError);
    return;
  }
};

export async function populateServicesDropdownFields(
  populateFormFieldsParams: PopulateFormFieldsParams,
) {
  const { form, dataForFieldPopulation } = populateFormFieldsParams;
  const services = dataForFieldPopulation.servicesData?.services;

  const servicesDropdownFields = getFormServicesDropdownFields(form);
  servicesDropdownFields.forEach((field) => {
    field.view!.options = services?.map((service) => ({
      id: service.id,
      label: service.name,
      value: service.id,
    }));
  });
}
